import React, { useState, useEffect, useMemo } from "react";
import { X } from "lucide-react";
import { RcsTemplate } from "./rcs-template-picker";
import { RcsComponent } from "../../../marketing/templates/rcs/rcs-types";

interface RcsTemplateVariableModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: RcsTemplate | null;
  // Called with content_variables object (for API) and preview components
  onConfirm: (contentVariables: Record<string, string>, previewComponents: RcsComponent[]) => void;
}

// Extract variables from RCS template components
// Supports both numeric {{1}} and named {{first_name}} variables
function extractRcsVariables(components: RcsComponent[]): string[] {
  const variables = new Set<string>();

  components.forEach((component) => {
    const c = component as any;
    // Check text content (API uses 'text', types use 'content')
    const textContent = c.content || c.text;
    if (component.type === "text" && textContent) {
      const matches = textContent.matchAll(/{{(\w+)}}/g);
      for (const match of matches) {
        variables.add(match[1]);
      }
    }

    // Check rich card title and description
    if (component.type === "rich_card") {
      if (component.title) {
        const titleMatches = component.title.matchAll(/{{(\w+)}}/g);
        for (const match of titleMatches) {
          variables.add(match[1]);
        }
      }
      if (component.description) {
        const descMatches = component.description.matchAll(/{{(\w+)}}/g);
        for (const match of descMatches) {
          variables.add(match[1]);
        }
      }
    }

    // Check carousel cards
    if (component.type === "carousel" && component.cards) {
      component.cards.forEach((card) => {
        if (card.title) {
          const titleMatches = card.title.matchAll(/{{(\w+)}}/g);
          for (const match of titleMatches) {
            variables.add(match[1]);
          }
        }
        if (card.description) {
          const descMatches = card.description.matchAll(/{{(\w+)}}/g);
          for (const match of descMatches) {
            variables.add(match[1]);
          }
        }
      });
    }
  });

  // Sort: numbers first (numerically), then strings alphabetically
  return [...variables].sort((a, b) => {
    const aNum = parseInt(a);
    const bNum = parseInt(b);
    if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
    if (!isNaN(aNum)) return -1;
    if (!isNaN(bNum)) return 1;
    return a.localeCompare(b);
  });
}

// Replace variables in components for preview
// Supports both numeric {{1}} and named {{first_name}} variables
function replaceVariablesInComponents(
  components: RcsComponent[],
  values: Record<string, string>
): RcsComponent[] {
  return components.map((component) => {
    const newComponent = { ...component } as any;
    const c = component as any;

    // Handle text component (API uses 'text', types use 'content')
    const textContent = c.content || c.text;
    if (component.type === "text" && textContent) {
      const replaced = textContent.replace(
        /{{(\w+)}}/g,
        (match: string, key: string) => values[key] || match
      );
      // Set both fields so preview works regardless of which one is used
      newComponent.content = replaced;
      newComponent.text = replaced;
    }

    if (component.type === "rich_card") {
      if (component.title) {
        newComponent.title = component.title.replace(
          /{{(\w+)}}/g,
          (match: string, key: string) => values[key] || match
        );
      }
      if (component.description) {
        newComponent.description = component.description.replace(
          /{{(\w+)}}/g,
          (match: string, key: string) => values[key] || match
        );
      }
    }

    if (component.type === "carousel" && component.cards) {
      newComponent.cards = component.cards.map((card) => ({
        ...card,
        title: card.title?.replace(/{{(\w+)}}/g, (match: string, key: string) => values[key] || match),
        description: card.description?.replace(/{{(\w+)}}/g, (match: string, key: string) => values[key] || match),
      }));
    }

    return newComponent;
  });
}

export const RcsTemplateVariableModal: React.FC<RcsTemplateVariableModalProps> = ({
  isOpen,
  onClose,
  template,
  onConfirm,
}) => {
  const variableIndices = useMemo(() => {
    if (!template?.components) return [];
    return extractRcsVariables(template.components);
  }, [template]);

  const [values, setValues] = useState<Record<string, string>>({});

  // Initialize values from template examples
  useEffect(() => {
    if (!template) return;

    const initial: Record<string, string> = {};
    variableIndices.forEach((idx) => {
      // Try to get example from template.variables if available
      const varDef = template.variables?.find((v) => v.index === parseInt(idx));
      initial[idx] = varDef?.example || "";
    });
    setValues(initial);
  }, [template, isOpen, variableIndices]);

  // Auto-confirm if no variables (safety net - ChatArea should skip this modal entirely)
  useEffect(() => {
    if (isOpen && template && variableIndices.length === 0) {
      onConfirm({}, template.components || []);
      onClose();
    }
  }, [isOpen, template, variableIndices.length]);

  if (!isOpen || !template || variableIndices.length === 0) return null;

  const handleChange = (index: string, val: string) => {
    setValues((prev) => ({ ...prev, [index]: val }));
  };

  const allFilled = variableIndices.every((idx) => (values[idx] ?? "").trim() !== "");

  // Build preview components with replaced values
  const previewComponents = replaceVariablesInComponents(template.components || [], values);

  // Get preview text for simple display
  const getPreviewText = (): string => {
    const textComponent = template.components?.find((c) => c.type === "text") as any;
    const textContent = textComponent?.content || textComponent?.text;
    if (textContent) {
      return textContent.replace(
        /{{(\w+)}}/g,
        (match: string, key: string) => values[key] || match
      );
    }

    const richCard = template.components?.find((c) => c.type === "rich_card");
    if (richCard) {
      const title = richCard.title?.replace(/{{(\w+)}}/g, (match: string, key: string) => values[key] || match) || "";
      const desc = richCard.description?.replace(/{{(\w+)}}/g, (match: string, key: string) => values[key] || match) || "";
      return `${title}\n${desc}`.trim();
    }

    return template.content || "";
  };

  const handleDone = () => {
    // Build content_variables object for API
    const contentVariables: Record<string, string> = {};
    variableIndices.forEach((idx) => {
      contentVariables[idx] = values[idx] || "";
    });

    onConfirm(contentVariables, previewComponents);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-[110] p-0 sm:p-4">
      <div className="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[92vh] flex flex-col overflow-hidden shadow-2xl">
        {/* Drag handle (mobile) */}
        <div className="sm:hidden flex justify-center pt-2.5 pb-1">
          <div className="w-10 h-1 rounded-full bg-slate-200 dark:bg-gray-700" />
        </div>

        {/* Header */}
        <div className="px-5 sm:px-6 pt-2 sm:pt-5 pb-3 sm:pb-4 border-b dark:border-gray-800 flex items-start justify-between">
          <div>
            <h2 className="text-base sm:text-lg font-bold text-slate-900 dark:text-gray-100">
              Configure Template
            </h2>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-gray-400 mt-0.5">
              Fill in the variables for: {template.template_name}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 dark:text-gray-500 hover:text-slate-600 dark:hover:text-gray-300">
            <X size={22} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-4">
          {/* Preview */}
          <div className="bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-xl p-3.5 mb-5">
            <p className="text-sm text-slate-700 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">
              {getPreviewText()}
            </p>
          </div>

          {/* Variable inputs */}
          <div className="flex flex-col gap-4">
            {variableIndices.map((idx) => {
              const varDef = template.variables?.find((v) => v.index === parseInt(idx));
              const filled = (values[idx] ?? "").trim() !== "";

              return (
                <div key={idx}>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-sm font-semibold text-slate-700 dark:text-gray-200">
                      Variable {idx} {varDef?.name && `(${varDef.name})`}
                    </label>
                    {filled && (
                      <span className="text-xs font-semibold text-green-600">Set</span>
                    )}
                  </div>
                  <input
                    value={values[idx] ?? ""}
                    onChange={(e) => handleChange(idx, e.target.value)}
                    placeholder={varDef?.example || `Enter value for {{${idx}}}`}
                    className="w-full border border-slate-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 dark:focus:ring-indigo-500/30"
                  />
                  {varDef?.example && (
                    <p className="text-[11px] text-slate-400 dark:text-gray-500 mt-1">
                      Example: {varDef.example}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 sm:px-6 py-4 border-t dark:border-gray-800 bg-white dark:bg-gray-900">
          <button
            onClick={handleDone}
            disabled={!allFilled}
            className="w-full bg-indigo-600 text-white rounded-xl py-3 font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
